import { useState, useEffect } from 'react';
import { useAuthStore } from '@/store/authStore';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Plus, Trash2, MapPin, User, Package, ChevronDown, ChevronUp } from 'lucide-react';
import {
  getAddresses,
  createAddress,
  deleteAddress,
  type Address,
  type AddressInput,
} from '@/services/erpnextAddress';
import {
  getOrders,
  getOrderDetails,
  getOrderStatusLabel,
  getOrderStatusColor,
  type Order,
} from '@/services/erpnextOrders';

// Common countries for dropdown
const COUNTRIES = [
  'Canada',
  'United States',
  'United Kingdom',
  'Australia',
  'Germany',
  'France',
  'Italy',
  'Spain',
  'Netherlands',
  'Belgium',
  'Switzerland',
  'Austria',
  'Sweden',
  'Norway',
  'Denmark',
  'Finland',
  'Ireland',
  'New Zealand',
  'Singapore',
  'Japan',
  'South Korea',
  'Mexico',
  'Brazil',
  'Argentina',
  'India',
  'United Arab Emirates',
];

export const Account = () => {
  const user = useAuthStore((state) => state.user);
  const navigate = useNavigate();

  const [addresses, setAddresses] = useState<Address[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);

  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(true);
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [orderDetails, setOrderDetails] = useState<Record<string, Order>>({});

  // Address form state
  const [formData, setFormData] = useState<AddressInput>({
    address_title: '',
    address_type: 'Shipping',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    country: '',
    pincode: '',
    phone: '',
  });

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    loadAddresses();
    loadOrders();
  }, [user, navigate]);

  const loadAddresses = async () => {
    try {
      setIsLoading(true);
      const data = await getAddresses();
      console.log('Loaded addresses:', data);
      setAddresses(data);
    } catch (error) {
      console.error('Failed to load addresses:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadOrders = async () => {
    try {
      setIsLoadingOrders(true);
      const data = await getOrders();
      setOrders(data);
    } catch (error) {
      console.error('Failed to load orders:', error);
    } finally {
      setIsLoadingOrders(false);
    }
  };

  const toggleOrderDetails = async (orderId: string) => {
    if (expandedOrderId === orderId) {
      setExpandedOrderId(null);
      return;
    }

    setExpandedOrderId(orderId);

    // Fetch order details if not already loaded
    if (!orderDetails[orderId]) {
      try {
        const details = await getOrderDetails(orderId);
        setOrderDetails((prev) => ({ ...prev, [orderId]: details }));
      } catch (error) {
        console.error('Failed to load order details:', error);
        alert('Failed to load order details');
      }
    }
  };

  const handleAddAddress = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsAdding(true);
      await createAddress(formData);
      await loadAddresses();
      setShowAddressForm(false);
      // Reset form
      setFormData({
        address_title: '',
        address_type: 'Shipping',
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        country: '',
        pincode: '',
        phone: '',
      });
    } catch (error) {
      console.error('Failed to add address:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      // Show user-friendly error message
      if (errorMessage.includes('Country')) {
        alert('Invalid country. Please select a valid country from the dropdown.');
      } else {
        alert(`Failed to add address. Please try again.\n\nError: ${errorMessage}`);
      }
    } finally {
      setIsAdding(false);
    }
  };

  const handleDeleteAddress = async (addressName: string) => {
    if (!confirm('Are you sure you want to delete this address?')) {
      return;
    }

    try {
      await deleteAddress(addressName);
      await loadAddresses();
    } catch (error) {
      console.error('Failed to delete address:', error);
      alert('Failed to delete address. Please try again.');
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* User Info */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-brand-primary rounded-full flex items-center justify-center">
              <User className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{user.full_name}</h1>
              <p className="text-gray-600">{user.email}</p>
            </div>
          </div>
        </div>

        {/* Orders Section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2 mb-6">
            <Package className="w-5 h-5" />
            Order History
          </h2>

          {isLoadingOrders ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-600">No orders yet</p>
              <p className="text-sm text-gray-500 mt-1">
                Start shopping to see your orders here
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((order) => (
                <div
                  key={order.name}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  {/* Order Summary */}
                  <div
                    className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => toggleOrderDetails(order.name)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-gray-900">{order.name}</h3>
                          <span
                            className={`text-xs px-2 py-1 rounded ${getOrderStatusColor(
                              order
                            )}`}
                          >
                            {getOrderStatusLabel(order)}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <span>
                            {new Date(order.transaction_date).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </span>
                          <span>•</span>
                          <span className="font-medium text-gray-900">
                            {order.currency} ${order.grand_total.toFixed(2)}
                          </span>
                        </div>
                      </div>
                      <div>
                        {expandedOrderId === order.name ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Order Details (Expanded) */}
                  {expandedOrderId === order.name && orderDetails[order.name] && (
                    <div className="border-t border-gray-200 bg-gray-50 p-4">
                      <h4 className="font-semibold text-gray-900 mb-3">Order Items</h4>
                      <div className="space-y-3">
                        {orderDetails[order.name].items?.map((item, idx) => (
                          <div
                            key={idx}
                            className="flex items-start gap-3 p-3 bg-white rounded"
                          >
                            {item.image && (
                              <img
                                src={item.image}
                                alt={item.item_name}
                                className="w-16 h-16 object-cover rounded"
                              />
                            )}
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">{item.item_name}</p>
                              <p className="text-xs text-gray-500">{item.item_code}</p>
                              <p className="text-sm text-gray-600 mt-1">
                                Qty: {item.qty} × {order.currency} ${item.rate.toFixed(2)}
                              </p>
                            </div>
                            <p className="font-semibold text-gray-900">
                              {order.currency} ${item.amount.toFixed(2)}
                            </p>
                          </div>
                        ))}
                      </div>

                      {/* Delivery Date */}
                      {orderDetails[order.name].delivery_date && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <p className="text-sm text-gray-600">
                            Expected Delivery:{' '}
                            <span className="font-medium text-gray-900">
                              {new Date(
                                orderDetails[order.name].delivery_date!
                              ).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              })}
                            </span>
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Addresses Section */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Saved Addresses
            </h2>
            <Button
              onClick={() => setShowAddressForm(!showAddressForm)}
              className="bg-brand-primary hover:bg-brand-primary/90"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Address
            </Button>
          </div>

          {/* Add Address Form */}
          {showAddressForm && (
            <form onSubmit={handleAddAddress} className="mb-6 p-6 bg-gray-50 rounded-lg space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="address_title">Address Name</Label>
                  <Input
                    id="address_title"
                    value={formData.address_title}
                    onChange={(e) =>
                      setFormData({ ...formData, address_title: e.target.value })
                    }
                    placeholder="e.g., Home, Office"
                    required
                    className="bg-white"
                  />
                </div>

                <div>
                  <Label htmlFor="address_type">Type</Label>
                  <select
                    id="address_type"
                    value={formData.address_type}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        address_type: e.target.value as 'Billing' | 'Shipping',
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white"
                    required
                  >
                    <option value="Shipping">Shipping</option>
                    <option value="Billing">Billing</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+1 (555) 123-4567"
                    className="bg-white"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="address_line1">Address Line 1</Label>
                  <Input
                    id="address_line1"
                    value={formData.address_line1}
                    onChange={(e) =>
                      setFormData({ ...formData, address_line1: e.target.value })
                    }
                    placeholder="Street address"
                    required
                    className="bg-white"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="address_line2">Address Line 2 (Optional)</Label>
                  <Input
                    id="address_line2"
                    value={formData.address_line2}
                    onChange={(e) =>
                      setFormData({ ...formData, address_line2: e.target.value })
                    }
                    placeholder="Apartment, suite, etc."
                    className="bg-white"
                  />
                </div>

                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    required
                    className="bg-white"
                  />
                </div>

                <div>
                  <Label htmlFor="state">Province/State</Label>
                  <Input
                    id="state"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    className="bg-white"
                  />
                </div>

                <div>
                  <Label htmlFor="country">Country</Label>
                  <select
                    id="country"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900"
                    required
                  >
                    <option value="">Select a country...</option>
                    {COUNTRIES.map((country) => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label htmlFor="pincode">Postal/ZIP Code</Label>
                  <Input
                    id="pincode"
                    value={formData.pincode}
                    onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                    required
                    className="bg-white"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <Button type="submit" disabled={isAdding} className="bg-brand-primary">
                  {isAdding ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Address'
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddressForm(false)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}

          {/* Address List */}
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : addresses.length === 0 ? (
            <div className="text-center py-12">
              <MapPin className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-600">No saved addresses yet</p>
              <p className="text-sm text-gray-500 mt-1">
                Add an address to speed up checkout
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {addresses.map((address) => (
                <div
                  key={address.name}
                  className="p-4 border border-gray-200 rounded-lg hover:border-brand-primary transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-gray-900">{address.address_title}</h3>
                        {address.address_type && (
                          <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                            {address.address_type}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600 leading-relaxed">
                        {address.display
                          ?.replace(/<br\s*\/?>/g, '\n')
                          .trim()
                          .split('\n')
                          .filter((line) => line.trim())
                          .map((line, idx) => (
                            <div key={idx}>{line}</div>
                          ))}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteAddress(address.name)}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
